import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Autocomplete,
  InputAdornment,
  Alert,
  Collapse,
  IconButton,
  AlertTitle,
} from "@mui/material";
import { InlineIcon } from "@iconify/react";
import LoadingButton from "@mui/lab/LoadingButton";
import { useFormik } from "formik";
import * as yup from "yup";
import * as moment from "moment";
import "moment/locale/fr";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import { routePaths } from "@/config";
import { Toastify } from "@/utils/toast";
import {
  TCity,
  TCountry,
  TFiche,
  TMemberInfos,
} from "@/features/profile/types";
import {
  apiGetCitiesByCountry,
  apiSaveFiche,
  // Pour charger les pays : soit un appel API, soit un JSON local
  // apiGetCountries,
} from "@/features/profile/api/profile.api";
import {
  selectMemberInfos,
  selectMemberProfile,
} from "@/store/reducers/member/member.selector";
import { TMemberProfile } from "@/features/auth/types";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/store";
import { updateMemberProfile } from "@/store/reducers/member/member.actions";
import useUserRestrictions from "@/hooks/useUserRestriction";
import CModalRejectedModif from "@/components/Modal-Rejected-Modif/ModalRejectedModif";
import "./Fiche.styles.scss";
import { useGooglePlacesAutocomplete } from "@/hooks/useGooglePlacesAutocomplete";

// =======================================================================
// Composant Fiche - version optimisée pour l'autocomplete de ville
// =======================================================================
const Fiche: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Sélecteurs depuis le store
  const memberInfosFromStore = useSelector(selectMemberInfos) as TMemberInfos;
  const memberProfileFromStore = useSelector(
    selectMemberProfile
  ) as TMemberProfile;

  // Hook de restriction d'utilisateur
  const {
    isUserProfileModificationApproved,
    isUserProfileModificationRejected,
    isUserProfileModificationPending,
    isUserSponsored,
    didUserHasToPay,
  } = useUserRestrictions();

  // =======================================================
  // États pour la liste des pays et villes
  // =======================================================
  const [countries, setCountries] = useState<TCountry[]>([]);
  const [cities, setCities] = useState<TCity[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCity, setLoadingCity] = useState(false);

  const villeRef = useRef<HTMLInputElement | null>(null);

  // Stockage de l'objet pays sélectionné (pour icône de drapeau, etc.)
  const [countryObj, setCountryObj] = useState<TCountry>({
    country: memberProfileFromStore?.infos?.identity?.country || "",
    abbreviation: memberProfileFromStore?.infos?.identity?.countryCode || "",
  });

  // Stockage de l'objet ville sélectionnée
  const [cityObj, setCityObj] = useState<TCity>({
    name: memberProfileFromStore?.infos?.identity?.city || "",
    id: memberProfileFromStore?.infos?.identity?.city || "",
  });

  // Nouvel état pour la saisie dans l'autocomplete de ville
  const [citySearch, setCitySearch] = useState<string>(
    memberProfileFromStore?.infos?.identity?.city || ""
  );

  // =======================================================
  // Chargement des pays (ici via un JSON local)
  // =======================================================
  const getCountries = useCallback(() => {
    const countries_json_data = require("@/assets/json/countries.json");
    setCountries(countries_json_data);
  }, []);

  useEffect(() => {
    getCountries();
  }, [getCountries]);

  // =======================================================
  // Fonction debounced pour charger les villes
  // =======================================================

  const { apiGetCitiesByCountryfromHook, isLoaded, loadError } =
    useGooglePlacesAutocomplete();

  const fetchCities = useCallback(
    async (countryAbbrev: string, prefix: string) => {
      if (!countryAbbrev || !prefix) {
        setCities([]);
        return;
      }
      try {
        setLoadingCity(true);
        const response = await apiGetCitiesByCountryfromHook(
          countryAbbrev,
          prefix
        );

        console.log(response);

        const data = response?.data?.predictions || [];

        const arr = data.map((one: any) => ({
          name: one.structured_formatting.main_text,
          id: one.place_id,
        }));

        setCities(arr);
      } catch (error) {
        console.error(error);
        // Toastify("Erreur lors du chargement des villes", "error", 3000);
      } finally {
        setLoadingCity(false);
      }
    },
    [apiGetCitiesByCountryfromHook]
  );

  // Déclenchement de la recherche dès que le pays ou la saisie change
  useEffect(() => {
    // Si la barre de recherche est vide, on vide la liste des villes et on ne lance pas d’appel
    if (!citySearch.trim()) {
      setCities([]);
      return;
    }

    // On crée un timer local (pas dans le state)
    const timer = setTimeout(() => {
      fetchCities(countryObj.abbreviation, citySearch);
    }, 500);

    // On nettoie le timer si l’utilisateur retape avant 500 ms
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citySearch, countryObj.abbreviation]);

  // =======================================================
  // Validation du formulaire avec Yup
  // =======================================================
  const validationSchema = yup.object({
    username: yup
      .string()
      .test({
        name: "not-email",
        message: "Le pseudo ne doit pas être un email.",
        test(value) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return !emailRegex.test(value ?? "");
        },
      })
      .required("Le pseudo est obligatoire."),
    birthday_date: yup
      .date()
      .required("L'âge est obligatoire.")
      .min(
        new Date(new Date().getFullYear() - 90, 0, 1),
        "Vous devez avoir moins de 90 ans"
      )
      .max(
        new Date(new Date().getFullYear() - 18, 11, 31),
        "Vous devez avoir plus de 18 ans"
      ),
    country: yup
      .string()
      .required("Le pays est obligatoire.")
      .test("valideCountry", "Ce pays n'est pas valide", (value) => {
        if (!value) return false;
        return countries.some((c) => c.country === value);
      }),
    cityOfCountry: yup
      .string()
      .required("La ville est obligatoire.")
      .test("valideCity", "Cette ville n'est pas valide", (value) => {
        if (!value) return false;
        return cities.some((c) => c.name === value);
      }),
  });

  // =======================================================
  // Formik : initialisation et soumission du formulaire
  // =======================================================
  const formik = useFormik({
    initialValues: {
      username:
        memberProfileFromStore?.infos?.identity?.username ||
        memberInfosFromStore?.username ||
        "",
      birthday_date: moment
        .default(memberProfileFromStore?.infos?.identity?.birthDate)
        .format("YYYY-MM-DD"),
      country: memberProfileFromStore?.infos?.identity?.country || "",
      cityOfCountry: memberProfileFromStore?.infos?.identity?.city || "",
    },
    validationSchema,
    onSubmit: (values) => {
      // Vérification si des changements ont été faits
      const initialValuesTrimmed = {
        username: formik.initialValues.username.trim(),
        birthday_date: formik.initialValues.birthday_date,
        country: formik.initialValues.country.trim(),
        cityOfCountry: formik.initialValues.cityOfCountry.trim(),
      };
      const actualValuesTrimmed = {
        username: values.username.trim(),
        birthday_date: values.birthday_date,
        country: values.country.trim(),
        cityOfCountry: values.cityOfCountry.trim(),
      };

      if (_.isEqual(initialValuesTrimmed, actualValuesTrimmed)) {
        Toastify("Aucun changement apporté à votre fiche.", "warning", 3000);
        return;
      }

      // Extraction des coordonnées depuis cityObj.id (format: "city|lat|lng")
      const [nameOfCity, lat, lng] = cityObj.id.split("|");

      const dataToSend: TFiche = {
        category: "identity",
        birthDate: values.birthday_date + "T00:00:00.000Z",
        city: values.cityOfCountry,
        country: values.country,
        countryCode: countryObj.abbreviation || "",
        username: values.username.trim(),
        cityLocation: {
          latitude: lat,
          longitude: lng,
        },
      };

      // Utilisation d'un état distinct pour le chargement de la sauvegarde
      setLoadingSave(true);
      apiSaveFiche(dataToSend)
        .then((response: any) => {
          setLoadingSave(false);
          const profile = response.data as TMemberProfile;
          dispatch(updateMemberProfile({ ...profile }));
          navigate(routePaths.profileChildren.myAccountChildren.formAbsolute, {
            replace: false,
          });
          Toastify(
            "Votre fiche a été enregistrée avec succès.",
            "success",
            5000
          );
        })
        .catch((error) => {
          setLoadingSave(false);
          console.error(error);
          const msg =
            error?.response?.data?.message || "Une erreur est survenue";
          Toastify(msg, "error", 5000);
        });
    },
  });

  // =======================================================
  // États supplémentaires pour l'affichage et alertes
  // =======================================================
  const [loadingSave, setLoadingSave] = useState(false);
  const [openAlertForUpdate, setOpenAlertForUpdate] = useState(true);
  const [openModerationReport, setOpenModerationReport] = useState(false);

  // Fonction de navigation
  const goTo = (routes: string) => {
    navigate(routes, { replace: true });
  };

  // =======================================================
  // Au chargement du composant je cherche à retrouver la ville en mémoire
  // =======================================================

  useEffect(() => {
    const lastcityRegistered = [
      {
        name: memberProfileFromStore?.infos?.identity?.city || "",
        id: memberProfileFromStore?.infos?.identity?.city || "",
      },
    ];
    setCities([...lastcityRegistered]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =======================================================
  // Rendu du composant
  // =======================================================
  return (
    <>
      <div className="ct-fiche">
        {/* Navigation part */}
        <div className="ct-fiche__nav-part">
          <Button
            color="primary"
            className="ct-fiche__nav-part__btn-nav"
            onClick={() =>
              goTo(routePaths.profileChildren.myAccountChildren.formAbsolute)
            }
            startIcon={
              <InlineIcon icon="material-symbols:lab-profile-outline" />
            }
          >
            Accédez à votre profil
          </Button>
          {!didUserHasToPay &&
            memberProfileFromStore?.account?.subscription?.lastPayment
              ?.purchaseId !== null &&
            memberProfileFromStore?.approbation?.isApproved !== null && (
              <>
                <Button
                  color="primary"
                  className="ct-formulaire__nav-part__btn-nav sponsor-button"
                  onClick={() => {
                    goTo(
                      routePaths.profileChildren.myAccountChildren
                        .sponsorAbsolute
                    );
                  }}
                  startIcon={
                    <InlineIcon
                      icon={
                        isUserSponsored
                          ? "gridicons:stats"
                          : "ic:twotone-rocket-launch"
                      }
                    />
                  }
                >
                  {isUserSponsored ? `État de mon boost` : `Booster mon profil`}
                </Button>
              </>
            )}

          {!isUserProfileModificationApproved && (
            <>
              {!openAlertForUpdate && (
                <IconButton
                  size="small"
                  color={
                    isUserProfileModificationRejected
                      ? memberProfileFromStore?.status?.reason?.length > 0
                        ? "error"
                        : "warning"
                      : "info"
                  }
                  onClick={() => setOpenAlertForUpdate((prev) => !prev)}
                  className="icon-blip"
                >
                  <InlineIcon
                    icon={
                      isUserProfileModificationRejected
                        ? memberProfileFromStore?.status?.reason?.length > 0
                          ? "material-symbols:error-outline-rounded"
                          : "material-symbols:warning-outline-rounded"
                        : "material-symbols:info-outline-rounded"
                    }
                  />
                </IconButton>
              )}
              {memberProfileFromStore?.status?.reason?.length > 0 && (
                <Button
                  color="warning"
                  className="ct-fiche__nav-part__btn-nav report"
                  onClick={() => setOpenModerationReport(true)}
                  startIcon={<InlineIcon icon="fluent-mdl2:report-warning" />}
                >
                  Rapport de modération
                </Button>
              )}
            </>
          )}
        </div>

        {/* Body part */}
        <div className="ct-fiche__body-part">
          {!isUserProfileModificationApproved && (
            <Collapse in={openAlertForUpdate}>
              <Alert
                severity={
                  isUserProfileModificationRejected
                    ? memberProfileFromStore?.status?.reason?.length > 0
                      ? "error"
                      : "warning"
                    : "info"
                }
                onClose={() => setOpenAlertForUpdate((prev) => !prev)}
                className="ct-formulaire__body-part__alert"
              >
                {isUserProfileModificationRejected && (
                  <>
                    {memberProfileFromStore?.status?.reason?.length > 0 ? (
                      <>
                        <AlertTitle>Modifications rejetées</AlertTitle>
                        <>
                          Les modifications apportées à votre profil ont été{" "}
                          <b> rejetées</b> par notre équipe de modération pour
                          la(les) raison(s) suivante(s):
                          <ul
                            style={{
                              textAlign: "left",
                              paddingLeft: "20px",
                              paddingTop: "20px",
                              marginBottom: "20px",
                            }}
                          >
                            {memberProfileFromStore?.status?.reason?.map(
                              (x, index) => (
                                <li className="li-reason" key={index}>
                                  {x.category === "identity" && (
                                    <>
                                      Dans la{" "}
                                      <b
                                        onClick={() => {
                                          navigate(
                                            routePaths.profileChildren
                                              .myAccountChildren.ficheAbsolute,
                                            { replace: true }
                                          );
                                        }}
                                      >
                                        <InlineIcon
                                          icon="akar-icons:link-out"
                                          fontSize={12}
                                        />
                                        Fiche d'information
                                      </b>
                                      :
                                    </>
                                  )}
                                  {x.category === "profile" && (
                                    <>
                                      Dans la rubrique{" "}
                                      <b
                                        onClick={() => {
                                          navigate(
                                            routePaths.profileChildren
                                              .myAccountChildren.formAbsolute,
                                            {
                                              replace: true,
                                              state: { formStep: "1" },
                                            }
                                          );
                                        }}
                                      >
                                        <InlineIcon
                                          icon="akar-icons:link-out"
                                          fontSize={12}
                                        />
                                        Profil
                                      </b>
                                      :
                                    </>
                                  )}
                                  {x.category === "religiousAspects" && (
                                    <>
                                      Dans la rubrique{" "}
                                      <b
                                        onClick={() => {
                                          navigate(
                                            routePaths.profileChildren
                                              .myAccountChildren.formAbsolute,
                                            {
                                              replace: true,
                                              state: { formStep: "2" },
                                            }
                                          );
                                        }}
                                      >
                                        <InlineIcon
                                          icon="akar-icons:link-out"
                                          fontSize={12}
                                        />
                                        Aspect religieux
                                      </b>
                                      :
                                    </>
                                  )}
                                  {x.category === "religiousPractices" && (
                                    <>
                                      Dans la rubrique{" "}
                                      <b
                                        onClick={() => {
                                          navigate(
                                            routePaths.profileChildren
                                              .myAccountChildren.formAbsolute,
                                            {
                                              replace: true,
                                              state: { formStep: "3" },
                                            }
                                          );
                                        }}
                                      >
                                        <InlineIcon
                                          icon="akar-icons:link-out"
                                          fontSize={12}
                                        />
                                        Pratiques religieuses
                                      </b>
                                      :
                                    </>
                                  )}
                                  {x.category === "healthAndDisability" && (
                                    <>
                                      Dans la rubrique{" "}
                                      <b
                                        onClick={() => {
                                          navigate(
                                            routePaths.profileChildren
                                              .myAccountChildren.formAbsolute,
                                            {
                                              replace: true,
                                              state: { formStep: "4" },
                                            }
                                          );
                                        }}
                                      >
                                        <InlineIcon
                                          icon="akar-icons:link-out"
                                          fontSize={12}
                                        />
                                        Santé et handicap
                                      </b>
                                      :
                                    </>
                                  )}
                                  <b className="value">{x.value}</b>
                                </li>
                              )
                            )}
                          </ul>
                          <br />
                          <strong>
                            {" "}
                            Veuillez apporter les corrections adéquates à vos
                            modifications pour qu'elles puissent être prises en
                            compte.
                          </strong>
                          <br />
                          Avant l'approbation de vos modifications , votre
                          profil sera inaccessible aux autres membres
                          <br />
                        </>
                      </>
                    ) : (
                      <>
                        <AlertTitle>Profil en cours de modération</AlertTitle>
                        <>
                          Les modifications apportées à votre profil sont
                          toujours <b> en cours de vérification</b> par notre
                          équipe de modération.
                          <br />
                          Avant l'approbation de vos modifications , votre
                          profil sera inaccessible aux autres membres.
                          <br />
                        </>
                      </>
                    )}
                  </>
                )}
                {isUserProfileModificationPending && (
                  <>
                    Vous avez effectué des modifications dans votre profil.
                    <br />
                    <strong>
                      {" "}
                      Vos modifications sont donc en cours de modération.
                    </strong>
                    <br />
                    Durant la modération, votre profil est inaccessible aux
                    autres membres.
                    <br />
                  </>
                )}
              </Alert>
            </Collapse>
          )}
          <div className="ct-fiche__body-part__card-part">
            <h4>
              <b>MA FICHE D'INFORMATION</b>
            </h4>
            <img
              src={require(
                "@/assets/images/png/avatar-" +
                  (memberProfileFromStore?.infos?.identity?.gender || "MALE") +
                  ".png"
              )}
              alt="sidebar-logo"
              className="ct-fiche__card-part__avatar"
            />
            {/* Formulaire */}
            <form className="form-part" onSubmit={formik.handleSubmit}>
              <Box className="upper-box">
                <Grid container rowSpacing={4} columnSpacing={2}>
                  <Grid item xs={12} sm={12} lg={6}>
                    <FormControl
                      fullWidth
                      color="primary"
                      className="gender-radio-row"
                    >
                      <FormLabel id="id-civilité">Civilité</FormLabel>
                      <RadioGroup
                        row
                        aria-labelledby="id-civilité"
                        name="row-radio-buttons-group"
                        value={
                          memberProfileFromStore?.infos?.identity?.gender ||
                          "MALE"
                        }
                      >
                        <FormControlLabel
                          value="MALE"
                          disabled
                          control={<Radio />}
                          label="Mr"
                        />
                        <FormControlLabel
                          value="FEMALE"
                          disabled
                          control={<Radio />}
                          label="Mme"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={12} lg={6}>
                    <TextField
                      placeholder="Pseudo"
                      variant="outlined"
                      label="Pseudo*"
                      name="username"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.username}
                      error={
                        formik.touched.username &&
                        Boolean(formik.errors.username)
                      }
                      helperText={
                        formik.touched.username && formik.errors.username
                      }
                      type="text"
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box>
                <Grid container rowSpacing={4} columnSpacing={2}>
                  <Grid item xs={12} sm={12} lg={6}>
                    <TextField
                      placeholder="Prénom"
                      variant="outlined"
                      label="Prénom**"
                      value={
                        memberProfileFromStore?.infos?.identity?.firstname || ""
                      }
                      disabled
                      type="text"
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} lg={6}>
                    <TextField
                      placeholder="Nom"
                      variant="outlined"
                      label="Nom**"
                      value={
                        memberProfileFromStore?.infos?.identity?.lastname || ""
                      }
                      disabled
                      type="text"
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} lg={12}>
                    <TextField
                      placeholder="Votre adresse email"
                      variant="outlined"
                      label="Email"
                      value={memberInfosFromStore?.email || ""}
                      disabled
                      type="text"
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} lg={6}>
                    <TextField
                      placeholder="Votre date de naissance"
                      variant="outlined"
                      label="Date de naissance*"
                      name="birthday_date"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.birthday_date}
                      error={
                        formik.touched.birthday_date &&
                        Boolean(formik.errors.birthday_date)
                      }
                      helperText={
                        formik.touched.birthday_date &&
                        formik.errors.birthday_date
                      }
                      type="date"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">  </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} lg={6}>
                    <TextField
                      placeholder="Votre numéro de téléphone"
                      variant="outlined"
                      label="Numéro de téléphone"
                      value={
                        memberProfileFromStore?.infos?.identity?.phoneNumber ||
                        ""
                      }
                      disabled
                      type="text"
                    />
                  </Grid>
                  {/* AUTOCOMPLETE PAYS */}
                  <Grid item xs={12} sm={12} lg={6}>
                    {loadingCountries ? (
                      <img
                        src={require("@/assets/images/gif/loadmore.gif")}
                        height="70"
                        width="70"
                        className="load-more-home"
                        alt="load-more"
                      />
                    ) : (
                      <Autocomplete
                        freeSolo={false}
                        autoHighlight
                        options={countries}
                        value={
                          countries.find(
                            (c) =>
                              c.country.replace(/’|'/g, "'") ===
                              formik.values.country.replace(/’|'/g, "'")
                          ) || null
                        }
                        getOptionLabel={(option: TCountry) => option.country}
                        onChange={(_event, newValue) => {
                          if (newValue) {
                            setCountryObj(newValue);
                            formik.setFieldValue("country", newValue.country);
                            if (newValue.abbreviation) {
                              // Au changement de pays, on déclenche la recherche de villes via debounce
                              setCitySearch(""); // Réinitialise la saisie pour éviter de garder un prefix obsolète
                              setCities([]);
                            }
                          } else {
                            setCountryObj({ country: "", abbreviation: "" });
                            formik.setFieldValue("country", "");
                            setCities([]);
                          }
                        }}
                        onBlur={() => formik.setFieldTouched("country", true)}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <InlineIcon
                              style={{ borderRadius: "50%" }}
                              icon={`flag:${option.abbreviation.toLowerCase()}-1x1`}
                            />
                            &nbsp;{option.country}
                          </Box>
                        )}
                        renderInput={(params) => (
                          <>
                            {countryObj.abbreviation && (
                              <InlineIcon
                                className="flag"
                                icon={`flag:${countryObj.abbreviation.toLowerCase()}-1x1`}
                              />
                            )}
                            <TextField
                              {...params}
                              label="Pays*"
                              placeholder="Votre pays"
                              error={
                                formik.touched.country &&
                                Boolean(formik.errors.country)
                              }
                              helperText={
                                formik.touched.country && formik.errors.country
                              }
                            />
                          </>
                        )}
                      />
                    )}
                  </Grid>
                  {/* AUTOCOMPLETE VILLE avec recherche dynamique */}
                  <Grid item xs={12} sm={12} lg={6}>
                    <Autocomplete
                      freeSolo={false}
                      autoHighlight
                      loading={loadingCity}
                      options={cities}
                      loadingText="Chargement..."
                      noOptionsText={
                        // Si aucun pays, invite à en sélectionner un
                        !formik.values.country.trim()
                          ? "Veuillez d'abord sélectionner un pays"
                          : citySearch.trim() === ""
                            ? "Commencez à taper un nom de ville"
                            : "Aucune ville trouvée"
                      }
                      value={
                        cities.find(
                          (c) => c.name === formik.values.cityOfCountry
                        ) || null
                      }
                      getOptionLabel={(option: TCity) => option.name}
                      onChange={(_event, newValue) => {
                        if (newValue) {
                          setCityObj(newValue);
                          formik.setFieldValue("cityOfCountry", newValue.name);
                        } else {
                          setCityObj({ name: "", id: "" });
                          formik.setFieldValue("cityOfCountry", "");
                        }
                      }}
                      onInputChange={(_event, newInputValue) => {
                        // Met à jour la saisie et déclenche le debounced fetch
                        setCitySearch(newInputValue);
                      }}
                      onBlur={() =>
                        formik.setFieldTouched("cityOfCountry", true)
                      }
                      renderInput={(params) => (
                        <TextField
                          ref={villeRef}
                          {...params}
                          label="Ville*"
                          placeholder="Votre ville"
                          error={
                            formik.touched.cityOfCountry &&
                            Boolean(formik.errors.cityOfCountry)
                          }
                          helperText={
                            formik.touched.cityOfCountry &&
                            formik.errors.cityOfCountry
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={12}
                    lg={12}
                    sx={{ textAlign: "center" }}
                  >
                    <Typography variant="caption" color="primary">
                      (**) INFORMATIONS <b>UNIQUEMENT</b> VISIBLES PAR
                      L’ADMINISTRATION DE ZAWAJ SUNNAH
                    </Typography>
                    <br />
                    <Typography variant="caption" color="primary">
                      (*) CHAMP OBLIGATOIRE
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              {/* Bouton d'enregistrement */}
              <LoadingButton
                loading={loadingSave}
                loadingPosition="center"
                variant="contained"
                color="tertiary"
                sx={{ marginTop: "30px" }}
                type="submit"
              >
                ENREGISTRER
              </LoadingButton>
            </form>
          </div>
        </div>
      </div>
      {/* Modal de rapport de modération */}
      {openModerationReport && (
        <CModalRejectedModif
          open={openModerationReport}
          setOpen={setOpenModerationReport}
        />
      )}
    </>
  );
};

export default Fiche;
